import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Trash2, Edit, Check, ArrowLeft } from 'lucide-react';
import { lvProductService } from '@/features/product-library/services/lvProductService';
import { toast } from 'sonner';
import { ProductSelectionModal } from '@/features/product-library/components';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import { useLVProducts } from '@/features/product-library/hooks/useLVProducts';
import type { LVProduct } from '@/features/product-library/types';
import { Switch } from '@/components/ui/switch';
import { useGroupProductsV2 } from '@/features/product-library/hooks/useGroupProductsV2';
import { GroupProductLibraryList } from './GroupProductLibraryList';
import { GroupProductSelectionModal } from './GroupProductSelectionModal';
import { Plus } from 'lucide-react';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';
import {
  useMergedCategoriesHierarchy,
  type DomainType,
} from '@/features/product-library/hooks/useMergedCategories';
import { CategoryEditSection } from './shared/CategoryEditSection';
import { IconSelector, type IconData } from '@/components/shared/IconSelector';
import { productLibraryService } from '@/features/product-library/services/productLibraryService';
import { organizationCategoryService } from '@/features/product-library/services/organizationCategoryService';
import type { MarkerSizesConfig } from '@/types/floorPlanViewer';

export function ProductDetailsDrawer() {
  // Zustand store state
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const previousDrawer = useAppStore(state => state.ui.previousDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const selectedProducts = useAppStore(state => state.selection.products);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const showProductLabels = useAppStore(state => state.products.showLabels);
  const setProductLabelsVisible = useAppStore(state => state.setProductLabelsVisible);

  // FloorPlan context data
  const { data, floorPlan, incrementCategoryIconVersion } = useFloorPlanContext();
  const { refetch: refetchProducts } = useLVProducts(floorPlan.id);
  const queryClient = useQueryClient();

  // Get baseScale from floor plan metadata for proper icon sizing
  const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
  const lvProductsBaseScale = markerSizes?.lv_products_base_scale ?? 1.0;

  // Local component state
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [productToEdit, setProductToEdit] = useState<LVProduct | null>(null);
  const [showAddProductsModal, setShowAddProductsModal] = useState(false);

  // Resize state
  const [resizeScope, setResizeScope] = useState<'single' | 'category' | 'subcategory'>('single');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [currentScale, setCurrentScale] = useState(1.0);
  const [hasUnsavedScale, setHasUnsavedScale] = useState(false);

  // Icon customization state
  const [isSavingIcon, setIsSavingIcon] = useState(false);

  // Computed values - MUST come before using selectedProduct
  const totalSelections =
    selectedProducts.size +
    useAppStore.getState().selection.fixtures.size +
    useAppStore.getState().selection.controls.size;
  const isOpen =
    activeDrawer === 'product' &&
    selectedProducts.size > 0 &&
    (selectedProducts.size === 1 ? totalSelections === 1 : true);
  const selectedCount = selectedProducts.size;
  const selectedProductId = selectedCount === 1 ? Array.from(selectedProducts)[0] : null;
  const selectedProduct = selectedProductId
    ? data.lvProducts?.find(p => p.id === selectedProductId)
    : null;

  // Determine domain based on selected product category - AFTER selectedProduct is defined
  const isLightingCategory =
    selectedProduct &&
    ['fixture', 'lighting', 'chandelier', 'pendant', 'recessed', 'track', 'wall_sconce'].includes(
      selectedProduct.category,
    );
  const categoryDomain = isLightingCategory ? 'lighting_fixtures' : 'lv_products';

  // Fetch merged categories (system + org) based on product type
  // This ensures we can find org-created subcategories like "Soundbar" under "Speaker"
  const { data: mergedCategories = [] } = useMergedCategoriesHierarchy(
    categoryDomain as DomainType,
  );

  // Get product info if product has a product_id
  const { data: linkedProduct } = useProduct(selectedProduct?.product_id || undefined);

  // Debug: Log linked product to check image_url
  useEffect(() => {
    // Always log when drawer opens with a selected product to help debug
    if (selectedProduct) {
      const hasImage =
        linkedProduct?.image_url ||
        (linkedProduct?.portal_metadata as any)?.imageUrl ||
        (selectedProduct?.metadata as any)?.portal_image_url;

      logger.log('[ProductDetailsDrawer] Debug info:', {
        selectedProduct: {
          id: selectedProduct.id,
          name: selectedProduct.name,
          product_id: selectedProduct.product_id,
          category: selectedProduct.category,
          metadata: selectedProduct.metadata,
        },
        linkedProduct: linkedProduct
          ? {
              id: linkedProduct.id,
              brand: linkedProduct.brand,
              model: linkedProduct.model,
              image_url: linkedProduct.image_url,
              portal_metadata: linkedProduct.portal_metadata,
            }
          : null,
        hasImage,
        shouldShowToggle: hasImage && selectedProduct?.category !== 'group',
      });
    }
  }, [linkedProduct, selectedProduct]);

  // Sync currentScale with selectedProduct
  useEffect(() => {
    if (selectedProduct) {
      setCurrentScale(selectedProduct.scale || 1.0);
      setHasUnsavedScale(false);
      setResizeScope('single');
      setSaveAsDefault(false);
    }
  }, [selectedProduct]);

  // Cleanup effect: Revert visual changes on unmount if there are unsaved changes
  useEffect(() => {
    return () => {
      // Only revert if there were unsaved changes
      if (hasUnsavedScale && selectedProduct) {
        const originalScale = selectedProduct.scale || 1.0;

        // Revert visual scale via DOM
        const iconContainer = document.querySelector(
          `[data-product-id="${selectedProduct.id}"] [data-icon-container]`,
        ) as HTMLElement;

        if (iconContainer) {
          const currentTransform = iconContainer.style.transform;
          const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
          const currentRotation = rotationMatch ? rotationMatch[1] : '0';
          iconContainer.style.transition = 'none';
          // Apply baseScale multiplier to match how LVProductLayer renders products
          iconContainer.style.transform = `scale(${originalScale * lvProductsBaseScale}) rotate(${currentRotation}deg)`;
        }

        // Revert rotation handle and label position using visual scale
        const visualScale = originalScale * lvProductsBaseScale;

        const rotationHandle = document.querySelector(
          `[data-product-rotation-handle="${selectedProduct.id}"]`,
        ) as HTMLElement;

        if (rotationHandle) {
          const iconRadius = 24;
          const margin = 11;
          const calculatedOffset = iconRadius * visualScale + margin;
          const rotationOffset = Math.max(calculatedOffset, 35);
          rotationHandle.style.top = `-${rotationOffset}px`;
        }

        // Revert label position
        const labelElement = document.querySelector(
          `[data-product-label="${selectedProduct.id}"]`,
        ) as HTMLElement;

        if (labelElement) {
          const labelMarginTop = 6 + (visualScale - 1.0) * 10;
          labelElement.style.marginTop = `${labelMarginTop}px`;
        }
      }
    };
  }, [hasUnsavedScale, selectedProduct]);

  // Get group products if this is a group
  const {
    groupProducts,
    isLoadingGroupProducts,
    addProductsToGroup,
    removeProductFromGroup,
    updateProductName,
    isAddingProducts,
    isRemovingProduct,
    isUpdatingName,
  } = useGroupProductsV2({
    groupId: selectedProduct?.id || '',
  });

  // Get room info
  const productRoom = selectedProduct?.room_id
    ? data.rooms?.find(r => r.id === selectedProduct.room_id)
    : null;

  // Map hyphenated categories to underscored categories for product library
  const mapCategoryToProductLibrary = (category: string): string => {
    const categoryMap: Record<string, string> = {
      'access-point': 'access_point',
      'touch-panel': 'touch_panel',
      touch_panel: 'touch_panel',
      access_point: 'access_point',
      camera: 'camera',
      speaker: 'speaker',
      doorbell: 'doorbell',
      display: 'display',
      Display: 'display',
      microphone: 'microphone',
      Microphones: 'microphone',
      thermostat: 'thermostat',
      Thermostat: 'thermostat',
      electrical: 'electrical',
      Electrical: 'electrical',
      sensor: 'sensor',
      other: 'other',
    };
    const mapped = categoryMap[category] || category;
    return mapped;
  };

  // Handle close
  const handleClose = () => {
    clearEntitySelection('product');
    // Only clear activeDrawer if no other entities are being selected
    // This allows smooth switching between entity drawers
    const state = useAppStore.getState();
    const hasOtherSelection =
      state.selection.fixtures.size > 0 ||
      state.selection.controls.size > 0 ||
      state.selection.rooms.size > 0 ||
      state.selection.circuits.size > 0 ||
      state.selection.panels.size > 0;

    if (!hasOtherSelection) {
      setActiveDrawer(null);
    }
  };

  // Handle back to portal drawer
  const handleBackToPortal = () => {
    clearEntitySelection('product');
    setActiveDrawer('portalProducts');
  };

  // Handle update product
  const handleUpdateProduct = async (updates: Partial<LVProduct>) => {
    if (!selectedProduct) return;

    setIsUpdating(true);
    try {
      await lvProductService.update(selectedProduct.id, updates);
      await refetchProducts();
      toast.success('Product updated successfully');
    } catch (error) {
      logger.error('Failed to update product:', error);
      toast.error('Failed to update product');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedProduct) return;

    setIsUpdating(true);
    try {
      // Check if this is a Portal.io product
      const isPortalProduct = selectedProduct.metadata?.from_portal === true;
      const portalProductId = selectedProduct.metadata?.portal_product_id;

      if (isPortalProduct) {
        // Dispatch event to update Portal drawer before deletion
        window.dispatchEvent(
          new CustomEvent('portal-item-removed', {
            detail: {
              portalProductId: portalProductId,
              lvProductId: selectedProduct.id,
            },
          }),
        );
      }

      // Delete the product from floor plan
      await lvProductService.delete(selectedProduct.id);

      // Clear selection after deletion
      clearEntitySelection('product');
      setActiveDrawer(null);

      await refetchProducts();
      toast.success('Product deleted successfully');
    } catch (error) {
      logger.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle batch resize based on scope
  const handleBatchResize = async (scale: number) => {
    if (!selectedProduct) return;

    setIsUpdating(true);
    try {
      // Filter products based on scope
      const allProducts = data.lvProducts || [];
      const productsToResize = allProducts.filter(p => {
        if (resizeScope === 'single') {
          return p.id === selectedProduct.id;
        } else if (resizeScope === 'category') {
          return p.category === selectedProduct.category;
        } else {
          // subcategory scope
          return (
            p.category === selectedProduct.category && p.subcategory === selectedProduct.subcategory
          );
        }
      });

      // Update all products
      await Promise.all(productsToResize.map(p => lvProductService.update(p.id, { scale })));

      // Save as default if requested (per floor plan)
      if (saveAsDefault) {
        try {
          logger.info('Saving default scale for product (per floor plan):', {
            category: selectedProduct.category,
            subcategory: resizeScope === 'subcategory' ? selectedProduct.subcategory : undefined,
            floorPlanId: floorPlan.id,
          });
          await data.saveCategoryDefault(
            'product',
            selectedProduct.category,
            scale,
            resizeScope === 'subcategory' ? selectedProduct.subcategory : undefined,
          );
          toast.success(
            `Resized ${productsToResize.length} product${productsToResize.length > 1 ? 's' : ''} and saved as default on this floor plan`,
          );
        } catch (error) {
          logger.error('Failed to save default scale:', error);
          toast.error('Resized products but failed to save as default');
        }
      } else {
        toast.success(
          `Resized ${productsToResize.length} product${productsToResize.length > 1 ? 's' : ''} to ${Math.round(scale * 100)}%`,
        );
      }

      // Refetch data
      await refetchProducts();

      // Reset unsaved state
      setHasUnsavedScale(false);
    } catch (error) {
      logger.error('Failed to batch resize products:', error);
      toast.error('Failed to resize products');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cancel resize
  const handleCancelResize = () => {
    if (!selectedProduct) return;

    // Revert to original scale
    const originalScale = selectedProduct.scale || 1.0;
    setCurrentScale(originalScale);
    setHasUnsavedScale(false);

    // Revert visual scale
    const iconContainer = document.querySelector(
      `[data-product-id="${selectedProduct.id}"] [data-icon-container]`,
    ) as HTMLElement;
    if (iconContainer) {
      const currentTransform = iconContainer.style.transform;
      const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
      const currentRotation = rotationMatch ? rotationMatch[1] : '0';
      iconContainer.style.transition = 'none';
      // Apply baseScale multiplier to match how LVProductLayer renders products
      iconContainer.style.transform = `scale(${originalScale * lvProductsBaseScale}) rotate(${currentRotation}deg)`;
    }

    // Revert rotation handle and label position using visual scale
    const visualScale = originalScale * lvProductsBaseScale;

    const rotationHandle = document.querySelector(
      `[data-product-rotation-handle="${selectedProduct.id}"]`,
    ) as HTMLElement;
    if (rotationHandle) {
      const iconRadius = 24;
      const margin = 11;
      const calculatedOffset = iconRadius * visualScale + margin;
      const rotationOffset = Math.max(calculatedOffset, 35);
      rotationHandle.style.top = `-${rotationOffset}px`;
    }

    // Revert label position
    const labelElement = document.querySelector(
      `[data-product-label="${selectedProduct.id}"]`,
    ) as HTMLElement;
    if (labelElement) {
      const labelMarginTop = 6 + (visualScale - 1.0) * 10;
      labelElement.style.marginTop = `${labelMarginTop}px`;
    }
  };

  // Old delete handler - keeping for reference but not used
  const handleDeleteOld = async () => {
    if (!selectedProduct) return;

    setIsUpdating(true);
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      // Check if this is a Portal.io product (OLD SYSTEM)
      const portalInstanceId = selectedProduct.metadata?.portal_instance_id;
      const isSplitItem = selectedProduct.metadata?.is_split_item;
      const splitId = selectedProduct.metadata?.split_id;

      if (portalInstanceId) {
        if (isSplitItem && splitId) {
          // Handle split item removal
          const { error: updateError } = await supabase.rpc('update_split_item_placement', {
            p_instance_id: portalInstanceId,
            p_split_id: splitId,
            p_suffix: selectedProduct.metadata?.split_suffix || '',
            p_placed: false,
            p_av_product_id: null,
            p_position_x: null,
            p_position_y: null,
          });

          if (updateError) {
            logger.error('Error updating split item placement:', updateError);
            throw updateError;
          }

          // Dispatch split-specific removal event
          window.dispatchEvent(
            new CustomEvent('portal-item-removed', {
              detail: {
                instanceId: portalInstanceId,
                splitId: splitId,
              },
            }),
          );
        } else {
          // Regular Portal instance removal
          const { error: portalError } = await supabase
            .from('portal_proposal_instances')
            .update({
              is_placed: false,
              av_product_id: null,
              placed_at: null,
              position_x: null,
              position_y: null,
              placed_split_items: [], // Clear any split item placements
            })
            .eq('id', portalInstanceId);

          if (portalError) {
            logger.error('Error updating Portal instance:', portalError);
            throw portalError; // Throw to prevent product deletion if Portal update fails
          }

          // Dispatch event with instance ID to update Portal drawer
          window.dispatchEvent(
            new CustomEvent('portal-item-removed', {
              detail: { instanceId: portalInstanceId },
            }),
          );
        }
      }

      await lvProductService.delete(selectedProduct.id);
      handleClose();
      await refetchProducts();
      toast.success('Product deleted successfully');
    } catch (error) {
      logger.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle room assignment
  const handleRoomChange = async (value: string) => {
    if (!selectedProduct) return;
    const roomId = value === 'none' ? null : value;
    await handleUpdateProduct({ room_id: roomId });
  };

  // Handle name edit
  const handleStartEditName = () => {
    setNameDraft(selectedProduct?.name || '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!selectedProduct || !nameDraft.trim()) return;
    await handleUpdateProduct({ name: nameDraft.trim() });
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setNameDraft('');
  };

  // Handle product selection from modal
  const handleProductSelect = async (product: any) => {
    if (!productToEdit) return;

    const productId = product?.id || null;

    setIsUpdating(true);
    try {
      await lvProductService.update(productToEdit.id, { product_id: productId });
      await refetchProducts();
      toast.success('Product updated successfully');
    } catch (error) {
      logger.error('Failed to update product:', error);
      toast.error('Failed to update product');
    } finally {
      setIsUpdating(false);
      setProductToEdit(null); // Clear after update
    }

    setShowProductModal(false);
  };

  // Handle unassigning product
  const handleUnassignProduct = async () => {
    if (!selectedProduct) return;

    setIsUpdating(true);
    try {
      // Check if this is a Portal.io product
      const isPortalProduct = selectedProduct.metadata?.from_portal === true;
      const portalProductId = selectedProduct.metadata?.portal_product_id;

      if (isPortalProduct) {
        // Generate generic name based on category
        const categoryLabel = selectedProduct.category.replace('-', ' ');
        const genericName = `${categoryLabel.charAt(0).toUpperCase() + categoryLabel.slice(1)} ${selectedProduct.id.slice(-4)}`;

        // Clear Portal metadata and revert to generic product
        const updatedMetadata = {
          ...selectedProduct.metadata,
          from_portal: false,
          portal_product_id: null,
          portal_name: null,
          portal_brand: null,
          portal_model: null,
          unassigned_at: new Date().toISOString(),
        };

        // Update the product to be generic
        await lvProductService.update(selectedProduct.id, {
          product_id: null,
          name: genericName,
          metadata: updatedMetadata,
        });

        // Dispatch event to update Portal drawer
        window.dispatchEvent(
          new CustomEvent('portal-item-unassigned', {
            detail: {
              portalProductId: portalProductId,
              lvProductId: selectedProduct.id,
            },
          }),
        );

        toast.success('Product unassigned and reverted to generic');
      } else {
        // Regular product unassignment - just clear the product_id
        const categoryLabel = selectedProduct.category.replace('-', ' ');
        const genericName = `${categoryLabel.charAt(0).toUpperCase() + categoryLabel.slice(1)} ${selectedProduct.id.slice(-4)}`;

        await lvProductService.update(selectedProduct.id, {
          product_id: null,
          name: genericName,
        });

        toast.success('Product unassigned successfully');
      }

      await refetchProducts();
    } catch (error) {
      logger.error('Failed to unassign product:', error);
      toast.error('Failed to unassign product');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Sheet
        open={isOpen}
        modal={false}
        onOpenChange={open => {
          if (!open && !showAddProductsModal && !showProductModal) {
            handleClose();
          }
        }}
      >
        <SheetContent
          className="w-[400px] overflow-y-auto [&>button]:hidden"
          side="right"
          onPointerDownOutside={e => {
            // Don't close if interacting with modals
            if (showAddProductsModal || showProductModal) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleClose();
          }}
          onInteractOutside={e => {
            // Don't close if interacting with modals
            if (showAddProductsModal || showProductModal) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleClose();
          }}
        >
          <SheetHeader>
            <div className="flex items-center gap-2">
              {previousDrawer === 'portalProducts' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBackToPortal}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <SheetTitle className="flex items-center gap-2 flex-1">
                {selectedProduct &&
                  (() => {
                    // Find the template for this product's category/subcategory
                    // Using merged categories (system + org) to find org-created subcategories
                    const template = selectedProduct.subcategory
                      ? mergedCategories.find(
                          t =>
                            t.category === selectedProduct.category &&
                            t.subcategory === selectedProduct.subcategory,
                        ) ||
                        mergedCategories.find(
                          t => t.category === selectedProduct.category && !t.subcategory,
                        )
                      : mergedCategories.find(
                          t => t.category === selectedProduct.category && !t.subcategory,
                        );

                    return (
                      <CategoryIconRenderer
                        category={selectedProduct.category}
                        template={template}
                        size={20}
                        className="text-foreground"
                      />
                    );
                  })()}
                Product Details
                {selectedCount > 1 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedCount} selected
                  </Badge>
                )}
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          {selectedProduct && (
            <div className="mt-6 space-y-6">
              {/* Product Name */}
              <div className="space-y-2">
                <Label>Name</Label>
                {isEditingName ? (
                  <div className="flex gap-2">
                    <Input
                      value={nameDraft}
                      onChange={e => setNameDraft(e.target.value)}
                      onKeyDown={e => {
                        e.stopPropagation(); // Prevent space-to-pan interference
                        if (e.key === 'Enter') handleSaveName();
                        if (e.key === 'Escape') handleCancelEditName();
                      }}
                      onKeyUp={e => e.stopPropagation()}
                      autoFocus
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleSaveName}
                      disabled={!nameDraft.trim()}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={handleCancelEditName}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{selectedProduct.name}</span>
                    <Button size="icon" variant="ghost" onClick={handleStartEditName}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Category & Subcategory */}
              <CategoryEditSection
                category={selectedProduct.category}
                subcategory={selectedProduct.subcategory}
                productType="lv_products"
                onSave={async (category, subcategory) => {
                  await handleUpdateProduct({
                    category,
                    subcategory,
                    category_info: {
                      ...selectedProduct.category_info,
                      category,
                      subcategory,
                    },
                  });

                  // Clear custom icon from linked product so it shows the new category's default icon
                  if (linkedProduct) {
                    try {
                      await productLibraryService.updateCustomIcon(linkedProduct.id, {
                        custom_icon_svg: null,
                        custom_icon_abbreviation: null,
                      });
                      // Invalidate product queries to refresh the IconSelector and floor plan icons
                      queryClient.invalidateQueries({
                        queryKey: ['product', linkedProduct.id],
                      });
                      queryClient.invalidateQueries({ queryKey: ['product-library'] });
                      queryClient.invalidateQueries({ queryKey: ['lv-products'] });
                    } catch (error) {
                      logger.error('Failed to clear custom icon:', error);
                    }
                  }
                }}
                isUpdating={isUpdating}
                disabled={selectedProduct.category === 'group'}
                label="Category"
              />

              {/* Room Assignment */}
              <div className="space-y-2">
                <Label>Room Assignment</Label>
                <Select
                  value={selectedProduct.room_id || 'none'}
                  onValueChange={handleRoomChange}
                  disabled={isUpdating}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a room" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Room</SelectItem>
                    {data.rooms?.map(room => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Product Selection - Not shown for groups */}
              {selectedProduct.category !== 'group' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Product Selection</Label>
                    {(() => {
                      // Check if this is a Portal.io imported product
                      const isPortalProduct = selectedProduct.metadata?.from_portal === true;

                      const button = (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setProductToEdit(selectedProduct);
                            setShowProductModal(true);
                          }}
                          disabled={isPortalProduct}
                        >
                          {linkedProduct ? 'Change Product' : 'Select Product'}
                        </Button>
                      );

                      if (isPortalProduct) {
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>{button}</TooltipTrigger>
                              <TooltipContent>
                                <p>Product is linked to Portal.io proposal</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }

                      return button;
                    })()}
                  </div>

                  {linkedProduct && (
                    <div className="p-4 bg-muted rounded-lg space-y-3">
                      {/* Product Image */}
                      {(() => {
                        // Try to get image URL from linked product or Portal metadata
                        const imageUrl =
                          linkedProduct?.image_url ||
                          (linkedProduct?.portal_metadata as any)?.imageUrl ||
                          (selectedProduct?.metadata as any)?.portal_image_url;

                        if (!imageUrl) return null;

                        return (
                          <div className="relative aspect-square w-24 h-24 mx-auto overflow-hidden rounded-lg border bg-muted">
                            <img
                              src={imageUrl}
                              alt={`${linkedProduct?.brand || selectedProduct?.name} ${linkedProduct?.model || ''}`}
                              className="h-full w-full object-contain p-1.5"
                              onError={e => {
                                // Hide image container if image fails to load
                                const container = e.currentTarget.parentElement;
                                if (container) {
                                  container.style.display = 'none';
                                }
                              }}
                            />
                          </div>
                        );
                      })()}

                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="font-medium">{linkedProduct.brand}</p>
                          <p className="text-sm text-muted-foreground">{linkedProduct.model}</p>
                          {linkedProduct.part_number && (
                            <p className="text-xs font-mono text-muted-foreground">
                              {linkedProduct.part_number}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleUnassignProduct}
                          disabled={isUpdating}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {linkedProduct.description && (
                        <p className="text-sm text-muted-foreground">{linkedProduct.description}</p>
                      )}
                    </div>
                  )}

                  {!linkedProduct && (
                    <div className="p-2 bg-muted/50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">
                        No product selected. This is a generic{' '}
                        {selectedProduct.category.replace('-', ' ')}.
                      </p>
                    </div>
                  )}

                  {/* Icon Section - Edit icon for ALL products of this category/subcategory */}
                  {(() => {
                    // Determine what we're editing: subcategory (if exists) or category
                    const hasSubcategory = !!selectedProduct.subcategory;

                    // Find the target category from merged categories
                    const targetCategory = hasSubcategory
                      ? mergedCategories.find(
                          c =>
                            c.category === selectedProduct.category &&
                            c.subcategory === selectedProduct.subcategory,
                        )
                      : mergedCategories.find(
                          c => c.category === selectedProduct.category && !c.subcategory,
                        );

                    // Count affected products
                    const affectedCount = (data.lvProducts || []).filter(p => {
                      if (hasSubcategory) {
                        return (
                          p.category === selectedProduct.category &&
                          p.subcategory === selectedProduct.subcategory
                        );
                      }
                      return p.category === selectedProduct.category;
                    }).length;

                    // Display name for the icon
                    const iconLabel =
                      targetCategory?.display_name ||
                      (hasSubcategory
                        ? selectedProduct.subcategory!.replace(/_/g, ' ').replace(/-/g, ' ')
                        : selectedProduct.category.replace(/_/g, ' ').replace(/-/g, ' '));

                    return (
                      <div className="p-4 bg-muted/50 rounded-lg space-y-3 mt-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Icon</Label>
                          <Badge variant="outline" className="text-xs">
                            {affectedCount} product{affectedCount !== 1 ? 's' : ''}
                          </Badge>
                        </div>

                        <IconSelector
                          currentIcon={{
                            iconType: targetCategory?.icon_svg
                              ? 'svg'
                              : targetCategory?.abbreviation
                                ? 'abbreviation'
                                : 'none',
                            iconSvg: targetCategory?.icon_svg,
                            abbreviation: targetCategory?.abbreviation,
                            fillColor: targetCategory?.fill_color,
                            borderColor: targetCategory?.border_color,
                            textColor: targetCategory?.text_color,
                            iconShape: targetCategory?.icon_shape,
                          }}
                          categoryIcon={{
                            icon_type: 'abbreviation' as const,
                            abbreviation: (hasSubcategory
                              ? selectedProduct.subcategory!
                              : selectedProduct.category
                            )
                              .slice(0, 3)
                              .toUpperCase(),
                            fill_color: targetCategory?.fill_color || '#6B7280',
                            border_color: targetCategory?.border_color || '#374151',
                            text_color: targetCategory?.text_color || '#FFFFFF',
                          }}
                          productName={iconLabel}
                          // Only show reset button if there's an org category override that can be reset
                          // System templates don't have org_category_id, so nothing to reset
                          showResetButton={!!targetCategory?.org_category_id}
                          onSave={async (iconData: IconData) => {
                            setIsSavingIcon(true);
                            try {
                              // Handle reset to default (iconType === 'none')
                              if (iconData.iconType === 'none') {
                                // If org category exists, reset its icon fields (but keep the category record)
                                if (targetCategory?.org_category_id) {
                                  await organizationCategoryService.resetIconToDefault(
                                    targetCategory.org_category_id,
                                  );
                                  toast.success(
                                    `Reset icon for all ${iconLabel} products to default`,
                                  );
                                } else {
                                  // No org category exists, already using system default
                                  toast.info(`Icon is already using the default`);
                                }
                              } else if (targetCategory?.org_category_id) {
                                // Update existing org category
                                await organizationCategoryService.updateCategory(
                                  targetCategory.org_category_id,
                                  {
                                    icon_type: iconData.iconType === 'svg' ? 'svg' : 'abbreviation',
                                    icon_svg: iconData.iconType === 'svg' ? iconData.iconSvg : null,
                                    abbreviation:
                                      iconData.iconType === 'abbreviation'
                                        ? iconData.abbreviation
                                        : null,
                                    fill_color: iconData.fillColor || '#6B7280',
                                    border_color: iconData.borderColor || '#374151',
                                    text_color: iconData.textColor || '#FFFFFF',
                                    icon_shape: iconData.iconShape || 'square',
                                  },
                                );
                                toast.success(`Updated icon for all ${iconLabel} products`);
                              } else {
                                // Create new org category to override system template
                                await organizationCategoryService.createCategory({
                                  domain: categoryDomain as
                                    | 'lv_products'
                                    | 'lighting_fixtures'
                                    | 'lighting_controls',
                                  category: selectedProduct.category,
                                  subcategory: hasSubcategory ? selectedProduct.subcategory : null,
                                  display_name: iconLabel,
                                  icon_type: iconData.iconType === 'svg' ? 'svg' : 'abbreviation',
                                  icon_svg: iconData.iconType === 'svg' ? iconData.iconSvg : null,
                                  abbreviation:
                                    iconData.iconType === 'abbreviation'
                                      ? iconData.abbreviation
                                      : null,
                                  fill_color: iconData.fillColor || '#6B7280',
                                  border_color: iconData.borderColor || '#374151',
                                  text_color: iconData.textColor || '#FFFFFF',
                                  icon_shape: iconData.iconShape || 'square',
                                });
                                toast.success(`Set icon for all ${iconLabel} products`);
                              }

                              // Invalidate all relevant caches
                              queryClient.invalidateQueries({ queryKey: ['merged-categories'] });
                              queryClient.invalidateQueries({
                                queryKey: ['organization-categories'],
                              });
                              queryClient.invalidateQueries({ queryKey: ['lv-products'] });
                              queryClient.invalidateQueries({ queryKey: ['product-library'] });
                              await refetchProducts();

                              // Trigger icon re-fetch in all ProductIcon components
                              incrementCategoryIconVersion();
                            } catch (error) {
                              logger.error('Failed to update icon:', error);
                              toast.error('Failed to update icon');
                            } finally {
                              setIsSavingIcon(false);
                            }
                          }}
                          isSaving={isSavingIcon}
                          previewSize={24}
                        />
                        <p className="text-xs text-muted-foreground">
                          Applies to all {iconLabel} products on floor plans.
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Group Contents - Only shown for groups */}
              {selectedProduct.category === 'group' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>
                      Group Contents{' '}
                      {groupProducts.length > 0 && (
                        <span className="text-muted-foreground ml-1">({groupProducts.length})</span>
                      )}
                    </Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowAddProductsModal(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Products
                    </Button>
                  </div>

                  <GroupProductLibraryList
                    products={groupProducts}
                    isLoading={isLoadingGroupProducts}
                    onRemoveProduct={removeProductFromGroup}
                    onUpdateProductName={updateProductName}
                    isRemovingProduct={isRemovingProduct}
                    isUpdatingName={isUpdatingName}
                  />
                </div>
              )}

              <Separator />

              {/* Size Controls */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Scale</Label>
                  <span className="text-sm font-medium">{Math.round(currentScale * 100)}%</span>
                </div>

                {/* Size Slider */}
                <Slider
                  min={0.2}
                  max={5.0}
                  step={0.05}
                  value={[currentScale]}
                  onValueChange={([value]) => {
                    // Update local state for slider thumb to move
                    setCurrentScale(value);
                    setHasUnsavedScale(true);

                    // Update visual scale immediately via DOM for selected product only
                    const iconContainer = document.querySelector(
                      `[data-product-id="${selectedProduct.id}"] [data-icon-container]`,
                    ) as HTMLElement;
                    if (iconContainer) {
                      const currentTransform = iconContainer.style.transform;
                      const rotationMatch = currentTransform.match(/rotate\(([-\d.]+)deg\)/);
                      const currentRotation = rotationMatch ? rotationMatch[1] : '0';
                      iconContainer.style.transition = 'transform 0.1s ease-out';
                      // Apply baseScale multiplier to match how LVProductLayer renders products
                      iconContainer.style.transform = `scale(${value * lvProductsBaseScale}) rotate(${currentRotation}deg)`;
                    }

                    // Update rotation handle and label position using visual scale
                    const visualScale = value * lvProductsBaseScale;

                    const rotationHandle = document.querySelector(
                      `[data-product-rotation-handle="${selectedProduct.id}"]`,
                    ) as HTMLElement;
                    if (rotationHandle) {
                      const iconRadius = 24;
                      const margin = 11;
                      const calculatedOffset = iconRadius * visualScale + margin;
                      const rotationOffset = Math.max(calculatedOffset, 35);
                      rotationHandle.style.top = `-${rotationOffset}px`;
                    }

                    // Update label position
                    const labelElement = document.querySelector(
                      `[data-product-label="${selectedProduct.id}"]`,
                    ) as HTMLElement;
                    if (labelElement) {
                      const labelMarginTop = 6 + (visualScale - 1.0) * 10;
                      labelElement.style.marginTop = `${labelMarginTop}px`;
                    }
                  }}
                  className="w-full"
                />

                {/* Scale range labels */}
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>20%</span>
                  <span>100%</span>
                  <span>500%</span>
                </div>

                {/* Apply to section - only show when there are unsaved changes */}
                {hasUnsavedScale && (
                  <div className="space-y-3 pt-2">
                    <Label className="text-sm">Apply to:</Label>

                    <RadioGroup
                      value={resizeScope}
                      onValueChange={(value: 'single' | 'category' | 'subcategory') =>
                        setResizeScope(value)
                      }
                    >
                      {/* Single product */}
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="single" id="resize-single" />
                        <label
                          htmlFor="resize-single"
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          This product only (1 item)
                        </label>
                      </div>

                      {/* Category */}
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="category" id="resize-category" />
                        <label
                          htmlFor="resize-category"
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          All {selectedProduct.category.replace(/_/g, ' ').replace(/-/g, ' ')}{' '}
                          products (
                          {
                            (data.lvProducts || []).filter(
                              p => p.category === selectedProduct.category,
                            ).length
                          }{' '}
                          items)
                        </label>
                      </div>

                      {/* Subcategory - only show if product has subcategory */}
                      {selectedProduct.subcategory && (
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="subcategory" id="resize-subcategory" />
                          <label
                            htmlFor="resize-subcategory"
                            className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            All {selectedProduct.category.replace(/_/g, ' ').replace(/-/g, ' ')}{' '}
                            &gt; {selectedProduct.subcategory.replace(/_/g, ' ').replace(/-/g, ' ')}{' '}
                            products (
                            {
                              (data.lvProducts || []).filter(
                                p =>
                                  p.category === selectedProduct.category &&
                                  p.subcategory === selectedProduct.subcategory,
                              ).length
                            }{' '}
                            items)
                          </label>
                        </div>
                      )}
                    </RadioGroup>

                    {/* Save as default checkbox */}
                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="save-default"
                        checked={saveAsDefault}
                        onCheckedChange={checked => setSaveAsDefault(checked as boolean)}
                      />
                      <label
                        htmlFor="save-default"
                        className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Save as default size
                      </label>
                    </div>

                    {/* Save/Cancel buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelResize}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleBatchResize(currentScale)}
                        disabled={isUpdating}
                        className="flex-1"
                      >
                        {isUpdating ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Display Settings */}
              <div className="space-y-4">
                <Label>Display Settings</Label>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Show Labels</span>
                  <Switch checked={showProductLabels} onCheckedChange={setProductLabelsVisible} />
                </div>

                {/* Product Image Callout - only show if product has an image */}
                {(() => {
                  const hasImage =
                    linkedProduct?.image_url ||
                    (linkedProduct?.portal_metadata as any)?.imageUrl ||
                    (selectedProduct?.metadata as any)?.portal_image_url;

                  if (!hasImage || selectedProduct?.category === 'group') return null;

                  return (
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-sm">Show Product Image</span>
                        <span className="text-xs text-muted-foreground">
                          Display image on floor plan
                        </span>
                      </div>
                      <Switch
                        checked={selectedProduct?.show_product_image || false}
                        onCheckedChange={async checked => {
                          if (!selectedProduct) return;
                          try {
                            // If enabling and no position set, calculate default position
                            const updates: Record<string, any> = {
                              show_product_image: checked,
                            };

                            if (checked && !selectedProduct.product_image_x) {
                              // Set default position offset from product
                              updates.product_image_x = selectedProduct.x_position + 100;
                              updates.product_image_y = selectedProduct.y_position - 100;
                              updates.product_image_width = 120;
                              updates.product_image_height = 120;
                            }

                            await handleUpdateProduct(updates);
                          } catch (error) {
                            logger.error('Failed to toggle product image:', error);
                            toast.error('Failed to update product image visibility');
                          }
                        }}
                        disabled={isUpdating}
                      />
                    </div>
                  );
                })()}
              </div>

              <Separator />

              {/* Actions */}
              <div className="space-y-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full" disabled={isUpdating}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Product
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Product?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this product from the floor plan. This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Product Selection Modal */}
      {showProductModal && productToEdit && (
        <ProductSelectionModal
          open={showProductModal}
          onOpenChange={setShowProductModal}
          onSelect={handleProductSelect}
          currentProductId={productToEdit.product_id || undefined}
          category={mapCategoryToProductLibrary(productToEdit.category) as any}
          subcategory={productToEdit.subcategory}
        />
      )}

      {/* Add Products to Group Modal */}
      <GroupProductSelectionModal
        open={showAddProductsModal && selectedProduct?.category === 'group'}
        onOpenChange={setShowAddProductsModal}
        onAddProducts={addProductsToGroup}
        groupName={selectedProduct?.name || ''}
        existingProductIds={groupProducts.map(p => p.product_id).filter((id): id is string => !!id)}
        isLoading={isAddingProducts}
      />
    </>
  );
}
